(() => {
  'use strict';
  const countries = { QA: 'QAR', SA: 'SAR', AE: 'AED', BH: 'BHD', KW: 'KWD', OM: 'OMR' };
  const supported = new Set(Object.values(countries));
  const labels = { QAR: 'ر.ق', SAR: 'ر.س', AED: 'د.إ', BHD: 'د.ب', KWD: 'د.ك', OMR: 'ر.ع' };
  const arabic = document.documentElement.lang.startsWith('ar');
  const locale = arabic ? 'ar-QA' : 'en';
  const preferenceKey = 'gh-display-currency-v1';
  const ratesKey = 'gh-currency-rates-v1';
  const geoKey = 'gh-currency-country-v1';
  const original = new WeakMap();
  const formatters = new Map();
  let currency = 'QAR';
  let mode = 'AUTO';
  let rates = null;
  let ratePromise = null;
  let geoPromise = null;
  let generation = 0;
  let scheduled = false;

  function read(storage, key) {
    try { return JSON.parse(storage.getItem(key)); } catch { return null; }
  }
  function save(storage, key, value) {
    try { storage.setItem(key, JSON.stringify(value)); } catch { /* Storage may be blocked. */ }
  }
  function remove(storage, key) {
    try { storage.removeItem(key); } catch { /* Storage may be blocked. */ }
  }
  // Access storage inside try/catch too: some browsers block the property getter.
  function storageRead(type, key) { try { return read(window[type], key); } catch { return null; } }
  function storageSave(type, key, value) { try { save(window[type], key, value); } catch {} }
  function storageRemove(type, key) { try { remove(window[type], key); } catch {} }
  function validRates(value) {
    return value && [...supported].every(code => Number.isFinite(value[code]) && value[code] > 0);
  }
  function loadRates() {
    if (rates) return Promise.resolve(rates);
    if (ratePromise) return ratePromise;
    const cached = storageRead('localStorage', ratesKey);
    if (cached && Date.now() - cached.at < 86400000 && validRates(cached.rates)) {
      rates = cached.rates;
      return Promise.resolve(rates);
    }
    ratePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.shopify.com/s/javascripts/currencies.js';
      script.async = true;
      script.fetchPriority = 'low';
      const timeout = setTimeout(() => finish(new Error('Currency rates unavailable')), 8000);
      function finish(error) {
        clearTimeout(timeout);
        script.onload = script.onerror = null;
        script.remove();
        if (error) { reject(error); return; }
        rates = Object.fromEntries([...supported].map(code => [code, window.Currency.rates[code]]));
        storageSave('localStorage', ratesKey, { at: Date.now(), rates });
        resolve(rates);
      }
      script.onload = () => finish(validRates(window.Currency?.rates) ? null : new Error('Invalid currency rates'));
      script.onerror = () => finish(new Error('Currency rates unavailable'));
      document.head.append(script);
    }).catch(error => { ratePromise = null; throw error; });
    return ratePromise;
  }
  function number(value, code) {
    const digits = ['BHD', 'KWD', 'OMR'].includes(code) ? 3 : 2;
    if (!formatters.has(code)) formatters.set(code, new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: digits }));
    return formatters.get(code).format(value);
  }
  function renderMoney(element) {
    const raw = element.getAttribute('data-gh-money');
    const amount = Number(raw);
    const base = element.getAttribute('data-gh-base-currency') || 'QAR';
    if (raw === null || raw.trim() === '' || !Number.isFinite(amount)) return;
    if (!original.has(element) || original.get(element).raw !== raw) original.set(element, { raw, html: element.innerHTML });
    // If native multicurrency is enabled later, leave its prices in charge.
    if (base !== 'QAR') return;
    if (currency === 'QAR' && !arabic) {
      const html = original.get(element).html;
      if (element.innerHTML !== html) element.innerHTML = html;
      return;
    }
    if (currency !== 'QAR' && !rates) return;
    const major = amount / 100 * (currency === 'QAR' ? 1 : rates.QAR / rates[currency]);
    const text = number(major, currency) + ' ' + (arabic ? labels[currency] : currency);
    if (element.textContent !== text) element.textContent = text;
  }
  function refresh() {
    scheduled = false;
    document.querySelectorAll('[data-gh-money]').forEach(renderMoney);
    document.querySelectorAll('[data-gh-currency-note]').forEach(note => { note.hidden = currency === 'QAR'; });
    document.querySelectorAll('[data-gh-currency-select]').forEach(select => {
      if (select.value !== mode) select.value = mode;
      const option = select.querySelector('[data-gh-currency-auto]');
      if (option) {
        const text = (arabic ? 'تلقائي' : 'Auto') + ' · ' + currency;
        if (option.textContent !== text) option.textContent = text;
      }
    });
  }
  function queueRefresh() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(refresh);
  }
  async function detectCountry() {
    const cached = storageRead('sessionStorage', geoKey);
    if (cached && Date.now() - cached.at < 21600000 && typeof cached.country === 'string') return cached.country;
    if (geoPromise) return geoPromise;
    geoPromise = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 6000);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'browsing_context_suggestions.json?country[enabled]=true', { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Country detection unavailable');
        const data = await response.json();
        const country = data.detected_values?.country?.handle || window.Shopify?.country || 'QA';
        storageSave('sessionStorage', geoKey, { at: Date.now(), country });
        return country;
      } catch { return window.Shopify?.country || 'QA'; }
      finally { clearTimeout(timeout); }
    })();
    return geoPromise;
  }
  async function choose(requested) {
    const ticket = ++generation;
    mode = requested;
    const target = requested === 'AUTO' ? countries[await detectCountry()] || 'QAR' : requested;
    if (ticket !== generation || !supported.has(target)) return;
    try {
      if (target !== 'QAR') await loadRates();
      if (ticket !== generation) return;
      currency = target;
    } catch {
      if (ticket !== generation) return;
      currency = 'QAR';
      mode = 'AUTO';
    }
    refresh();
  }
  document.addEventListener('change', event => {
    const select = event.target.closest?.('[data-gh-currency-select]');
    if (!select) return;
    const requested = select.value;
    if (requested !== 'AUTO' && !supported.has(requested)) return;
    if (requested === 'AUTO') storageRemove('localStorage', preferenceKey);
    else storageSave('localStorage', preferenceKey, requested);
    void choose(requested);
  });
  new MutationObserver(records => {
    if (records.some(record => {
      const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
      if (record.type === 'attributes' || target?.closest('[data-gh-money]')) return true;
      return [...record.addedNodes].some(node => node.nodeType === Node.ELEMENT_NODE && (node.matches('[data-gh-money], [data-gh-currency-select], [data-gh-currency-note]') || node.querySelector('[data-gh-money], [data-gh-currency-select], [data-gh-currency-note]')));
    })) queueRefresh();
  }).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['data-gh-money', 'data-gh-base-currency'] });

  document.querySelectorAll('[data-gh-currency-control]').forEach(control => { control.hidden = false; });
  refresh();
  const saved = storageRead('localStorage', preferenceKey);
  if (supported.has(saved)) void choose(saved);
  else {
    const start = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(() => { if (mode === 'AUTO') void choose('AUTO'); }, { timeout: 1500 });
      else setTimeout(() => { if (mode === 'AUTO') void choose('AUTO'); }, 0);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }
})();
